import { ChevronLeft, ChevronRight, Download, FileWarning, Loader2, Maximize2, Minimize2, X } from "lucide-react";
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { getAttachmentVersionInfo } from "@/components/chat/documentVersionEvents";
import { getApiBaseUrl } from "@/config/runtime";
import { getHeaders } from "@/lib/api";
import { createPanelBus } from "@/lib/panelBus";
import { chatService } from "@/lib/services/chat";

const PdfViewer = lazy(() => import("@/components/chat/PdfViewer").then((m) => ({ default: m.PdfViewer })));

import type { DocumentVersion } from "@/types/chat";

// ─── Event-based API for opening preview from non-React contexts ──────────

export type PreviewRequest = { attachmentUuid: string; filename: string };

const bus = createPanelBus<PreviewRequest>();

/** Open a document preview panel. Callable from anywhere (including Markdown component overrides). */
export function openDocumentPreview(attachmentUuid: string, filename: string): void {
  bus.open({ attachmentUuid, filename });
}

/** Close the document preview panel. Callable from anywhere. */
export const closeDocumentPreview = bus.close;

// ─── Helpers ──────────────────────────────────────────────────────────────

const PREVIEWABLE_EXTENSIONS = new Set(["pdf", "docx", "xlsx", "pptx"]);

/** Returns true if the filename has a previewable extension. */
export function isPreviewableFile(filename: string): boolean {
  const ext = filename.split(".").pop()?.toLowerCase() ?? "";
  return PREVIEWABLE_EXTENSIONS.has(ext);
}

/** Extract attachment UUID from an internal attachment path. */
export function extractAttachmentUuid(href: string): string | null {
  const match = href.match(/\/chat\/attachments\/([a-f0-9-]+)\/file/);
  return match ? match[1] : null;
}

// ─── Component ────────────────────────────────────────────────────────────

/**
 * Side panel that renders a PDF preview of a document inline next to the chat.
 *
 * For PDFs it renders directly; for DOCX/XLSX/PPTX files the backend
 * converts to PDF via LibreOffice. Mount this component as a flex sibling
 * of the main chat area in Chat.tsx. It listens for preview requests via
 * {@link openDocumentPreview}.
 *
 * When the previewed document belongs to a lineage (has multiple versions),
 * a revision navigation bar is shown with prev/next controls.
 */
export function DocumentPreviewPanel() {
  const [preview, setPreview] = useState<PreviewRequest | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const blobUrlRef = useRef<string | null>(null);

  // Lineage / revision navigation state
  const [versions, setVersions] = useState<DocumentVersion[] | null>(null);
  const [currentVersionIndex, setCurrentVersionIndex] = useState<number>(-1);

  // Subscribe to preview requests
  useEffect(() => {
    return bus.subscribe((req) => {
      if (!req) {
        handleClose();
        return;
      }
      // If clicking the same file again, just close the panel
      if (preview?.attachmentUuid === req.attachmentUuid) {
        handleClose();
        return;
      }
      setPreview(req);
      setError(null);
      setPdfUrl(null);
      setLoading(true);
      // Reset lineage state — will be populated after fetch
      setVersions(null);
      setCurrentVersionIndex(-1);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview?.attachmentUuid]);

  // Look up lineage info when a preview request comes in
  useEffect(() => {
    if (!preview) return;

    const versionInfo = getAttachmentVersionInfo(preview.attachmentUuid);
    if (!versionInfo) {
      // No lineage — standalone document
      setVersions(null);
      setCurrentVersionIndex(-1);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const res = await chatService.getSessionDocuments(versionInfo.sessionUuid);
        if (cancelled) return;

        // Find the lineage that matches
        const lineage = res.data.find((l) => l.uuid === versionInfo.lineageUuid);
        if (!lineage || lineage.versions.length <= 1) {
          // Only one version — no navigation needed
          setVersions(null);
          setCurrentVersionIndex(-1);
          return;
        }

        // Versions are sorted ascending by version number from the API
        const sorted = [...lineage.versions].sort((a, b) => a.version - b.version);
        setVersions(sorted);

        // Find current index
        const idx = sorted.findIndex((v) => v.attachmentUuid === preview.attachmentUuid);
        setCurrentVersionIndex(idx >= 0 ? idx : sorted.length - 1);
      } catch (err) {
        console.error("Failed to load lineage for preview navigation:", err);
        // Non-fatal — just don't show navigation
        setVersions(null);
        setCurrentVersionIndex(-1);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [preview]);

  // Fetch the PDF preview when a request comes in
  useEffect(() => {
    if (!preview || !loading) return;

    let cancelled = false;

    (async () => {
      try {
        const baseUrl = getApiBaseUrl();
        const headers = await getHeaders();
        headers.delete("Content-Type");

        const response = await fetch(`${baseUrl}/chat/attachments/${preview.attachmentUuid}/preview`, { headers });

        if (cancelled) return;

        if (!response.ok) {
          const text = await response.text();
          let detail = `HTTP ${response.status}`;
          try {
            const json = JSON.parse(text);
            detail = json.detail ?? detail;
          } catch {
            if (text) detail = text;
          }
          setError(detail);
          setLoading(false);
          return;
        }

        const blob = await response.blob();
        if (cancelled) return;

        // Revoke previous blob URL if switching files
        if (blobUrlRef.current) {
          URL.revokeObjectURL(blobUrlRef.current);
        }

        const url = URL.createObjectURL(blob);
        blobUrlRef.current = url;
        setPdfUrl(url);
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Failed to load preview");
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [preview, loading]);

  const handleClose = useCallback(() => {
    setPreview(null);
    setLoading(false);
    setError(null);
    setPdfUrl(null);
    setExpanded(false);
    setVersions(null);
    setCurrentVersionIndex(-1);
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
  }, []);

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, []);

  const handleDownload = useCallback(async () => {
    if (!preview) return;
    try {
      const baseUrl = getApiBaseUrl();
      const headers = await getHeaders();
      headers.delete("Content-Type");

      const response = await fetch(`${baseUrl}/chat/attachments/${preview.attachmentUuid}/file`, { headers });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = preview.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to download file:", err);
    }
  }, [preview]);

  // Navigate to a different version
  const navigateToVersion = useCallback(
    (index: number) => {
      if (!versions || index < 0 || index >= versions.length) return;
      const version = versions[index];
      // Trigger a new preview request for the target version's attachment
      setPreview({
        attachmentUuid: version.attachmentUuid,
        filename: version.filename,
      });
      setError(null);
      setPdfUrl(null);
      setLoading(true);
      setCurrentVersionIndex(index);
    },
    [versions],
  );

  const goToPrev = useCallback(() => {
    navigateToVersion(currentVersionIndex - 1);
  }, [navigateToVersion, currentVersionIndex]);

  const goToNext = useCallback(() => {
    navigateToVersion(currentVersionIndex + 1);
  }, [navigateToVersion, currentVersionIndex]);

  if (!preview) return null;

  const panelWidth = expanded ? "w-[60%]" : "w-[500px]";
  const hasRevisionNav = versions !== null && versions.length > 1 && currentVersionIndex >= 0;
  const hasPrev = hasRevisionNav && currentVersionIndex > 0;
  const hasNext = hasRevisionNav && currentVersionIndex < versions!.length - 1;

  return (
    <div
      className={`${panelWidth} flex flex-col border-l border-border-primary bg-surface-primary transition-all duration-300 min-w-0 overflow-hidden`}
    >
      {/* Header bar */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-border-primary bg-surface-secondary shrink-0">
        <span className="text-sm text-content-primary font-medium truncate mr-2">{preview.filename}</span>
        <div className="flex items-center gap-0.5 shrink-0">
          <button
            onClick={() => setExpanded((e) => !e)}
            className="p-1.5 hover:bg-interactive-active rounded text-content-secondary hover:text-content-primary transition-colors"
            title={expanded ? "Collapse panel" : "Expand panel"}
          >
            {expanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
          <button
            onClick={handleDownload}
            className="p-1.5 hover:bg-interactive-active rounded text-content-secondary hover:text-content-primary transition-colors"
            title="Download original file"
          >
            <Download className="w-4 h-4" />
          </button>
          <button
            onClick={handleClose}
            className="p-1.5 hover:bg-interactive-active rounded text-content-secondary hover:text-content-primary transition-colors"
            title="Close preview"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Revision navigation bar (only when document has multiple versions) */}
      {hasRevisionNav && (
        <div className="flex items-center justify-between px-3 py-1.5 border-b border-border-primary bg-surface-tertiary/50 shrink-0">
          <button
            onClick={goToPrev}
            disabled={!hasPrev}
            className="p-1 rounded hover:bg-interactive-active text-content-secondary hover:text-content-primary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title="Previous version"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-xs text-content-secondary font-medium">
            v{versions![currentVersionIndex].version} of {versions!.length}
          </span>
          <button
            onClick={goToNext}
            disabled={!hasNext}
            className="p-1 rounded hover:bg-interactive-active text-content-secondary hover:text-content-primary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title="Next version"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Body */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {loading && (
          <div className="flex flex-col items-center justify-center h-full gap-3">
            <Loader2 className="w-8 h-8 text-brand-primary animate-spin" />
            <span className="text-sm text-content-secondary">Generating preview...</span>
          </div>
        )}

        {error && (
          <div className="flex flex-col items-center justify-center h-full gap-3 px-4">
            <FileWarning className="w-8 h-8 text-amber-400" />
            <span className="text-sm text-content-secondary max-w-md text-center">{error}</span>
            <button
              onClick={handleDownload}
              className="mt-2 px-4 py-2 text-sm bg-brand-primary hover:bg-brand-primary/90 text-white rounded-lg transition-colors"
            >
              Download Original File
            </button>
          </div>
        )}

        {pdfUrl && !loading && !error && (
          <Suspense
            fallback={
              <div className="flex items-center justify-center h-full">
                <Loader2 className="animate-spin" size={24} />
              </div>
            }
          >
            <PdfViewer file={pdfUrl} title={preview.filename} />
          </Suspense>
        )}
      </div>
    </div>
  );
}

// ─── Backward-compatible export ───────────────────────────────────────────
// Keep the old name as an alias so existing imports don't break during transition.
export const DocumentPreviewModal = DocumentPreviewPanel;
